import React from 'react';
import { Share2, Linkedin, Twitter, Facebook, Link, Mail } from 'lucide-react';

const SocialShare = ({ 
  url, 
  title, 
  description = '', 
  showLabel = true,
  size = 'md',
  variant = 'default' 
}) => {
  const encodedUrl = encodeURIComponent(url || window.location.href);
  const encodedTitle = encodeURIComponent(title || document.title);
  const encodedDescription = encodeURIComponent(description);

  const shareLinks = {
    linkedin: `https://www.linkedin.com/sharing/share-offsite/?url=${encodedUrl}&title=${encodedTitle}&summary=${encodedDescription}`,
    twitter: `https://twitter.com/intent/tweet?url=${encodedUrl}&text=${encodedTitle}`,
    facebook: `https://www.facebook.com/sharer/sharer.php?u=${encodedUrl}&quote=${encodedTitle}`,
    email: `mailto:?subject=${encodedTitle}&body=${encodedDescription}%0A%0A${encodedUrl}`
  };

  const handleShare = (platform) => {
    if (platform === 'copy') {
      navigator.clipboard.writeText(url || window.location.href);
      // You could add a toast notification here
      return;
    }
    
    if (platform === 'native' && navigator.share) {
      navigator.share({
        title: title,
        text: description,
        url: url || window.location.href,
      });
      return;
    }
    
    const shareUrl = shareLinks[platform];
    if (shareUrl) {
      window.open(shareUrl, '_blank', 'width=600,height=400');
    }
  };

  const getSizeClasses = () => {
    switch (size) {
      case 'sm': return 'w-8 h-8';
      case 'lg': return 'w-12 h-12';
      default: return 'w-10 h-10';
    }
  };

  const getIconSize = () => {
    switch (size) {
      case 'sm': return 16;
      case 'lg': return 24;
      default: return 20;
    }
  };

  const buttonClass = `${getSizeClasses()} flex items-center justify-center rounded-full transition-all duration-200 ${
    variant === 'minimal' 
      ? 'hover:bg-gray-100 text-gray-600 hover:text-gray-800' 
      : 'hover:scale-110 hover:shadow-lg'
  }`;

  return (
    <div className="flex items-center space-x-3">
      {showLabel && (
        <span className="text-sm font-medium text-gray-700 flex items-center space-x-2">
          <Share2 size={16} />
          <span>Share:</span>
        </span>
      )}
      
      <div className="flex items-center space-x-2">
        {/* LinkedIn */}
        <button
          onClick={() => handleShare('linkedin')}
          className={`${buttonClass} bg-blue-600 text-white hover:bg-blue-700`}
          title="Share on LinkedIn"
          aria-label="Share on LinkedIn"
        >
          <Linkedin size={getIconSize()} />
        </button>

        {/* Twitter */}
        <button
          onClick={() => handleShare('twitter')}
          className={`${buttonClass} bg-sky-500 text-white hover:bg-sky-600`}
          title="Share on Twitter"
          aria-label="Share on Twitter"
        >
          <Twitter size={getIconSize()} />
        </button>

        {/* Facebook */}
        <button
          onClick={() => handleShare('facebook')}
          className={`${buttonClass} bg-blue-800 text-white hover:bg-blue-900`}
          title="Share on Facebook"
          aria-label="Share on Facebook"
        >
          <Facebook size={getIconSize()} />
        </button>

        {/* Email */}
        <button
          onClick={() => handleShare('email')}
          className={`${buttonClass} bg-gray-600 text-white hover:bg-gray-700`}
          title="Share via Email"
          aria-label="Share via Email"
        >
          <Mail size={getIconSize()} />
        </button>

        {/* Copy Link */}
        <button
          onClick={() => handleShare('copy')}
          className={`${buttonClass} bg-gray-400 text-white hover:bg-gray-500`}
          title="Copy Link"
          aria-label="Copy Link"
        >
          <Link size={getIconSize()} />
        </button>

        {/* Native Share (if supported) */}
        {navigator.share && (
          <button
            onClick={() => handleShare('native')}
            className={`${buttonClass} bg-green-600 text-white hover:bg-green-700`}
            title="Share"
            aria-label="Share"
          >
            <Share2 size={getIconSize()} />
          </button>
        )}
      </div>
    </div>
  );
};

export default SocialShare;